import { isArray, isFunction, isUndefined } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FieldErrors, FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import {
  ExtendedFieldValue,
  ExtendedFieldValueDict,
  FormField,
  FormOptionAnyMap,
  FormValuesMap,
  onComponentUpdate,
  onFormUpdate,
  OptionsParentProps,
  UseOptionHook,
} from './types';
import {
  coerceNumber,
  fieldKeyOrNull,
  initValueOrEmptyString,
  optionsOrUndefined,
} from './util';

const defaultTo = (defaultValue: any) => (value: any) =>
  value == null || value !== value ? defaultValue : value;

export const useFormComponentUpdate = (
  setValue: UseFormSetValue<ExtendedFieldValueDict>,
  onUpdate: onFormUpdate | undefined,
) =>
  useCallback(
    (value: ExtendedFieldValue, fieldKey: string) => {
      setValue(fieldKey, value);
      if (isFunction(onUpdate)) {
        onUpdate(fieldKey, value);
      }
    },
    [setValue],
  );

export const useOptions = (
  watch: UseFormWatch<ExtendedFieldValueDict>,
  useOptionHook: UseOptionHook<FormValuesMap>,
) => useOptionHook(watch());

export const useFields = (
  optionDict: FormOptionAnyMap,
  errorMap: FieldErrors<FieldValues>,
  initValuesMap: FormValuesMap,
  onUpdate: onComponentUpdate,
  disabled: boolean,
  formFields: FormField[],
) =>
  useMemo(
    () =>
      formFields.map(({ component: Component, ...rest }) => {
        const fieldKey = fieldKeyOrNull(rest) as null | string;
        const defaultInitValue = initValueOrEmptyString(rest) as ExtendedFieldValue;
        const defaultOptions = optionsOrUndefined(rest) as OptionsParentProps<string>;
        let initValue: ExtendedFieldValue,
          options: OptionsParentProps<string>,
          error: string | null;
        if (typeof fieldKey === 'string') {
          initValue = defaultTo(defaultInitValue)(initValuesMap[fieldKey]);
          options = defaultTo(defaultOptions)(optionDict[fieldKey]);
          const errorObj = errorMap[fieldKey] as any;
          error = errorObj?.message?.length > 0 ? errorObj.message : errorObj?.type;
        } else {
          initValue = defaultInitValue;
          options = defaultOptions;
          error = null;
        }
        if (
          !isArray(initValue) &&
          !isUndefined(initValue) &&
          typeof coerceNumber(fieldKey) === 'number'
        ) {
          initValue = String(initValue);
        }
        return (
          <Component
            key={`admin-form-${fieldKey}`}
            disabled={disabled}
            {...rest}
            fieldKey={fieldKey || ''}
            onChange={onUpdate}
            initValue={initValue}
            options={options}
            error={error}
          />
        );
      }),
    [optionDict, formFields, errorMap, initValuesMap, onUpdate, disabled],
  );
